// ── PremiumRow ────────────────────────────────────────────────────────────────
// A self-contained "Premium" row for the Settings panel: shows the signed-in
// user's plan status, a badge when active, and an Upgrade button that opens
// the full Premium page (PremiumGateModal) — same page every other "this is
// a Premium feature" prompt in the app opens, so there's one purchase flow,
// not several. Once Premium is active/trialing, the row itself becomes the
// tap target instead, expanding a read-only list of what's unlocked inline,
// right inside the Settings column (no popup) — just the labels of
// PREMIUM_EXTRAS, the same list the purchase page (PremiumGateModal) shows, so
// the two can't drift apart. Labels only, no descriptions (Jade, 2026-10-06:
// the old long-form list was too much here).
// Renders nothing when Firebase isn't configured or the user isn't signed in —
// premium is tied to an account so its status can sync across devices and be
// verified server-side.
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check } from 'lucide-react';
import { isFirebaseConfigured } from './config';
import { useAuth } from './auth';
import { usePremium, isNativeAndroid, guessIsPH, detectCountry, isLemonSqueezyEnabled } from './billing';
import { usePlayPurchasePending, useRenewingPlaySubscription } from './playBilling';
import PremiumGateModal from '../components/PremiumGateModal';
import { PREMIUM_EXTRAS } from './premiumFeatures';

function daysLeft(expiresAt) {
  return Math.max(1, Math.ceil((expiresAt - Date.now()) / (24 * 60 * 60 * 1000)));
}

export default function PremiumRow() {
  const { user } = useAuth();
  const { loading, isPremium, expiresAt, source, lifetime } = usePremium();
  // Trial signup itself is retired — isTrial only reflects a still-unexpired
  // grant made back when it existed, and naturally goes false once that
  // grant's premiumExpiresAt passes, same as any other premium grant.
  const isTrial = isPremium && source === 'trial';
  // True for the real few seconds between "Play reports this purchase
  // approved" and "our server finished verifying + granting it" — see
  // usePlayPurchasePending() in playBilling.js. Surfaced here so that window
  // reads as "confirming" instead of looking like nothing happened after
  // checkout closed.
  const purchasePending = usePlayPurchasePending();
  // Buying Lifetime doesn't cancel a subscription bought before it — Google
  // keeps charging until the user cancels in the Play Store. Only checked for
  // Lifetime holders, the one case where a renewing subscription is pure waste.
  const subscriptionStillRenewing = useRenewingPlaySubscription(lifetime);
  const [gateOpen, setGateOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [provider, setProvider] = useState(() => (guessIsPH() ? 'paymongo' : 'lemonsqueezy'));
  // Play policy forbids offering an alternative payment method inside the app
  // on Android — see PremiumGateModal.jsx for the matching guard — so the
  // Lemon-Squeezy-specific "coming soon" copy must never render there.
  const nativeAndroid = isNativeAndroid();
  const showComingSoon = !nativeAndroid && provider === 'lemonsqueezy' && !isLemonSqueezyEnabled();

  // Replace the instant timezone/locale guess with the real IP-based answer
  // once it comes back — silent, no user-facing toggle (see api/geo.js).
  // Skipped on native Android since that platform never shows PayMongo/Lemon
  // Squeezy at all — Play Billing handles it instead.
  useEffect(() => {
    if (nativeAndroid) return;
    let cancelled = false;
    detectCountry().then((country) => {
      if (!cancelled && country) setProvider(country === 'PH' ? 'paymongo' : 'lemonsqueezy');
    });
    return () => { cancelled = true; };
  }, [nativeAndroid]);

  // Surface the redirect-back result from either provider's success/cancel URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const upgrade = params.get('upgrade');
    if (!upgrade) return;
    if (upgrade === 'success') {
      toast.success('Payment received. Your premium access will unlock shortly.');
    } else if (upgrade === 'cancel') {
      toast('Checkout cancelled. No charge was made.');
    }
    params.delete('upgrade');
    const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
    window.history.replaceState({}, '', next);
  }, []);

  if (!isFirebaseConfigured || !user) return null;

  return (
    <div style={{ borderBottom: '1px solid var(--c-settings-line, #f5eaf0)' }}>
      <div
        onClick={isPremium ? () => setFeaturesOpen(v => !v) : undefined}
        style={{ padding: isPremium && featuresOpen ? '14px 20px 6px' : '14px 20px', display: 'flex', alignItems: 'center', gap: 12, cursor: isPremium ? 'pointer' : 'default' }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--c-text)' }}>
              {isTrial ? 'Clarity Free Trial' : 'Clarity Premium'}
            </span>
            {isPremium && (
              <span style={{
                fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4, color: 'var(--c-on-primary)',
                background: 'linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))',
                borderRadius: 999, padding: '2px 8px', textTransform: 'uppercase',
              }}>
                {isTrial ? '🎁 Free Trial' : lifetime ? '✨ Lifetime' : '✨ Active'}
              </span>
            )}
            {!isPremium && purchasePending && (
              <span style={{
                fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4, color: 'var(--c-text-accent)',
                background: 'var(--c-bg-soft)', border: '1px solid var(--c-border)',
                borderRadius: 999, padding: '2px 8px', textTransform: 'uppercase',
              }}>
                ⏳ Confirming
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'var(--c-text-soft)', marginTop: 2 }}>
            {loading
              ? 'Checking status…'
              : !isPremium && purchasePending
                ? 'Confirming your purchase with Google Play. This can take a few seconds…'
                : isTrial
                  ? `Trial ends in ${daysLeft(expiresAt)} day${daysLeft(expiresAt) === 1 ? '' : 's'} (${new Date(expiresAt).toLocaleDateString()})`
                  : lifetime
                    ? 'Lifetime Premium, yours for good'
                  : isPremium
                    ? null
                    : nativeAndroid
                      ? 'Unlock premium features'
                      : showComingSoon
                        ? '🇵🇭 Available in the Philippines for now. International coming soon'
                        : 'Unlock premium features via QR Ph'}
          </div>
        </div>

        {!loading && !isPremium && !purchasePending && (
          <button
            onClick={() => setGateOpen(true)}
            style={{
              flexShrink: 0, background: 'linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))',
              color: 'var(--c-on-primary)', border: 'none', borderRadius: 999,
              padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
            }}
          >
            {showComingSoon ? 'See Premium plans' : 'Upgrade'}
          </button>
        )}

        {isPremium && (
          <span style={{
            flexShrink: 0, fontSize: 12, color: 'var(--c-text-soft)', transition: 'transform 0.2s',
            transform: featuresOpen ? 'rotate(90deg)' : 'none',
          }}>▸</span>
        )}
      </div>

      {lifetime && subscriptionStillRenewing && (
        <div style={{
          margin: '0 20px 14px', padding: '10px 12px', borderRadius: 10, fontSize: 12, lineHeight: 1.5,
          color: 'var(--c-text)', background: 'var(--c-bg-soft)', border: '1px solid var(--c-border)',
        }}>
          <strong>You still have a subscription that renews.</strong> Lifetime already covers
          everything, so cancel it in the Play Store, under Payments &amp; subscriptions, or
          Google will keep charging you for it.
        </div>
      )}

      {gateOpen && <PremiumGateModal onClose={() => setGateOpen(false)} />}

      {featuresOpen && (
        <div style={{ padding: '10px 20px 16px', background: 'var(--c-bg-soft)' }}>
          <ul style={{ margin: 0, listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {PREMIUM_EXTRAS.map(({ label: f }) => (
              <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: 'var(--c-text)', lineHeight: 1.4 }}>
                <Check size={15} style={{ flexShrink: 0, marginTop: 1 }} color="var(--c-text-accent)" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
